import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";
import { FaShieldAlt, FaChartLine, FaRegCheckCircle, FaProjectDiagram, FaUserShield } from "react-icons/fa";

const RiskManagementPolicy: React.FC = () => {
  const policySections = [
    {
      title: "1. Strategic Resilience",
      icon: <FaShieldAlt />,
      content: `At ${import.meta.env.VITE_COMPANY_NAME}, our Risk Management Policy is built to ensure long-term stability. Under the oversight of our Board of Directors, we identify and mitigate potential business risks to protect the interests of our stakeholders and users.`,
    },
    {
      title: "2. Our Purpose",
      icon: <FaProjectDiagram />,
      content: `Framed in alignment with RBI's Risk Management Framework (DNBR. PD. 008/03.10.119/2016-17), this policy ensures that we grow responsibly while maintaining a secure environment for your financial transactions.`,
    },
    {
      title: "3. Core Principles",
      icon: <FaRegCheckCircle />,
      list: [
        "Value Protection: Every decision is weighted to protect user and shareholder value.",
        "Integrated Security: Risk management is embedded in our daily operational workflows.",
        "Data-Driven: We use quantitative assessment to stay ahead of market and credit risks.",
        "Adaptive Growth: Our framework evolves with the changing financial landscape.",
      ],
    },
    {
      title: "4. Managed Risk Verticals",
      icon: <FaChartLine />,
      list: [
        "Strategic Risk: Aligning long-term goals with market realities.",
        "Operational Risk: Ensuring 99.9% uptime and zero-error execution.",
        "Financial Risk: Maintaining healthy liquidity and capital adequacy.",
        "Credit Risk: Responsible lending through rigorous data assessment.",
        "Compliance Risk: Strict adherence to RBI and Indian financial laws.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO 
        title="Risk Management"
        description={`Our framework for stability and security. Learn how ${import.meta.env.VITE_COMPANY_NAME} manages risks to ensure a reliable lending platform.`}
        keywords={`risk management, financial security, stable fintech, NBFC compliance`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-gray-50/50 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10 pt-44 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          <header className="mb-24">
             <div className="inline-flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl rounded-full mb-10 transition-transform hover:scale-105">
                <FaUserShield className="text-secondary text-xs" />
                <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">Secure Infrastructure</span>
              </div>
            <h1 className="text-4xl md:text-8xl font-black tracking-tighter mb-10 text-text-primary leading-[0.9]">
              Built for <br />
              <span className="text-secondary">Stability.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-2xl font-medium leading-relaxed max-w-2xl">
              Our Risk Management framework is the foundation of our trust. We maintain institutional-grade security and financial health to ensure a reliable future for our users.
            </p>
          </header>

          <div className="space-y-12">
            {policySections.map((section, idx) => (
              <div key={idx} className="bg-white p-10 md:p-14 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl shrink-0">
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-4xl font-black mb-6 text-text-primary tracking-tight">
                      {section.title}
                    </h2>
                    {section.content && (
                      <p className="text-text-secondary text-lg md:text-xl leading-relaxed font-medium mb-6">
                        {section.content}
                      </p>
                    )}
                    {section.list && (
                      <ul className="space-y-4 pt-4">
                        {section.list.map((li, i) => (
                          <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-lg leading-relaxed">
                            <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Regulatory Confirmation */}
            <div className="bg-primary text-white p-12 md:p-20 rounded-[3rem] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-[80px]" />
                <h3 className="text-2xl md:text-4xl font-black mb-10 tracking-tighter">Institutional Resilience</h3>
                <p className="text-white/80 text-lg md:text-xl leading-relaxed font-medium mb-12">
                  At {import.meta.env.VITE_COMPANY_NAME}, risk is not just about avoidance; it is about managed excellence. Our Board-approved policy ensures that we maintain the highest liquidity standards while delivering industry-leading retail credit solutions.
                </p>
                <div className="flex items-center gap-6 pt-8 border-t border-white/20 font-black text-[10px] uppercase tracking-widest text-secondary">
                  <span>RBI Compliant Architecture</span>
                  <div className="w-1 h-1 bg-white/20 rounded-full" />
                  <span>ISO Standards Alignment</span>
                </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RiskManagementPolicy;
